/**
 * @file lib/billing/slotPlanner.js
 * @description How many active openings an agency wants, and what that costs on its CURRENT plan (owner design
 *   2026-10-18): Starter pays per extra slot beyond its free slots, at any count; Pro is a flat price with some
 *   openings included, then cheaper extras. The plan never changes on the slider: from the Pro break-even count
 *   (proBreakEvenCases, owner 2026-10-19: the same number the agency page shows) the result carries how much Pro
 *   would save, for a note. Pure math for the Billing slot slider; prices come from
 *   platform_config via the caller (getPlans, slotPriceFor). Display only: Stripe prices and the
 *   stripe-manage-extra-slots function decide the charge.
 *
 *   planSlots(input) -> { plan, count, extras, monthly, proMonthly, proSaves, tiles }
 *     proSaves: $/mo Pro would save at this count (Starter only, from the break-even count, only when Pro is cheaper; else 0)
 *     tiles: one per opening, kind "promo" | "starter" | "included" | "proExtra", used when an opening fills it
 * @module lib/billing/slotPlanner
 */

export const MAX_OPENINGS = 25;

/**
 * Smallest number of active openings N where paying the Starter slot price N times
 * costs at least as much as Pro for a month (N * starterSlotPrice >= proMonthly).
 * Returns null when the inputs are missing or when Pro would not pay off within its own
 * case limit, so callers can fall back to generic wording instead of a wrong number.
 * @param {number} starterSlotPrice monthly price of one Starter case slot
 * @param {number} proMonthly monthly Pro price
 * @param {number} [proCases] Pro included openings (break-even must fit inside it)
 * @param {number} [freeSlots] Starter openings that cost nothing (the launch offer's free ones; 0 once it ends)
 * @returns {number|null}
 */
export function proBreakEvenCases(starterSlotPrice, proMonthly, proCases, freeSlots = 0) {
  const slot = Number(starterSlotPrice);
  const pro = Number(proMonthly);
  if (!(slot > 0) || !(pro > 0)) return null;
  const n = Math.ceil(pro / slot) + Math.max(0, Number(freeSlots) || 0);
  if (n < 2) return null;
  if (Number(proCases) > 0 && n > Number(proCases)) return null;
  return n;
}

/**
 * @param {{ count: number, used?: number, currentPlan: "starter"|"pro", freeSlots: number, starterSlot: number,
 *           proPrice: number, proIncluded: number, proSlot: number, starterIncluded?: number }} input
 *   freeSlots: promo openings that cost nothing (the free period's openings; 0 once it ends). They stack on top of
 *   the paid Starter quantity while the promo runs.
 *   starterIncluded: new plan model only. Starter IS the extra-openings subscription, quantity N = Starter with its
 *   first opening included plus N-1 extra openings, every one at the Starter price, so the money is the same
 *   (paid openings x price). This only labels the first paid tile "included" (display). 0 = the old wording.
 */
export function planSlots({ count, used = 0, currentPlan, freeSlots, starterSlot, proPrice, proIncluded, proSlot, starterIncluded = 0 }) {
  const n = Math.max(0, Math.round(count));
  const proExtras = Math.max(0, n - proIncluded);
  const proMonthly = proPrice + proExtras * proSlot;
  if (currentPlan === "pro") {
    const tiles = Array.from({ length: n }, (_, i) => ({ kind: i < proIncluded ? "included" : "proExtra", used: i < used }));
    return { plan: "pro", count: n, extras: proExtras, monthly: proMonthly, proMonthly, proSaves: 0, tiles };
  }
  const extras = Math.max(0, n - freeSlots);
  const monthly = extras * starterSlot;
  const hintAt = proBreakEvenCases(starterSlot, proPrice, proIncluded, freeSlots);
  const proSaves = hintAt != null && n >= hintAt ? Math.max(0, monthly - proMonthly) : 0;
  const tiles = Array.from({ length: n }, (_, i) => ({
    kind: i < freeSlots ? "promo" : i < freeSlots + Math.min(1, starterIncluded) ? "included" : "starter",
    used: i < used,
  }));
  return { plan: "starter", count: n, extras, monthly, proMonthly, proSaves, tiles };
}

/** Lowest count the slider allows: the openings in use and the free ones, plus the Starter opening once Starter is
 *  active (it cannot go below 1: ending Starter is its own confirmed step). 0 when nothing is live and no plan. */
export function minOpenings({ used = 0, freeSlots = 0, currentPlan, proIncluded, starterIncluded = 0 }) {
  return Math.max(0, used, currentPlan === "pro" ? proIncluded : freeSlots + Math.min(1, starterIncluded));
}
